import { defineComponent, toRefs, computed, ref, PropType, onBeforeUpdate, nextTick } from '@vue/composition-api';
import { omit } from 'lodash-es';
import uuidv4 from 'uuid-v4';
import { i18n } from '@tt4b/ttam-i18n';
import { dataURLtoFile, getFileNameAndExtension, logger } from '@tt4b/ttam-utils';
import {
  FileUploadStatus,
  MaterialType,
  ImageToUpload,
  AdgroupFormData,
  FullNormalFormData,
  CampaignFormData,
  MaterialSize,
} from '@tt4b/ttam-enums';
import { useAllowlist, useMaterialVerify, useCheckNameExistServiceApi } from '@tt4b/ttam-hooks';

import ImageCropper from '../image-cropper/index.vue';
import FilesProgressTable from '../files-progress-table.vue';
import FilesSelectTable from '../files-select-table.vue';
import { trackEvent } from './../../../../utils/index';

enum PageStep {
  crop = 'crop',
  validate = 'validate',
  uploading = 'uploading',
  complete = 'complete',
}

/**
 * 处理裁剪图片文件名
 * 如果裁剪前后尺寸相同，保持文件名不变
 * 如果尺寸变化，由 a.png -> a_crop_[width]*[height].png
 */
function handleImageName(file: ImageToUpload) {
  const { name, isCropped, croppedCanvas } = file;
  if (!isCropped) {
    return name;
  }
  const { extension, excludeExtName } = getFileNameAndExtension(name);

  return `${excludeExtName}_crop_${croppedCanvas?.width}*${croppedCanvas?.height}${extension}`;
}

const componentName = 'ttam-material-image-upload-dialog';
export default defineComponent({
  name: componentName,
  components: { ImageCropper, FilesSelectTable, FilesProgressTable },

  props: {
    maxNum: { type: Number, default: 20 },
    /** 用来区分图片素材还是封面，创意：image，封面：cover，素材库：library */
    type: { type: String as PropType<'image' | 'cover' | 'library'>, default: 'image' },
    /** 是否需要版位校验 */
    inventoryCheck: { type: Boolean, default: true },
    /** 是否是上传封面模式 */
    isUploadCover: { type: Boolean, default: false },
    files: { type: Array as PropType<ImageToUpload[]>, default: () => [] },
    adgroupData: {
      required: true,
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    campaignData: {
      required: true,
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
    creativeData: {
      required: true,
      type: Object as PropType<FullNormalFormData>,
      default: () => ({}),
    },
  },
  setup(props, ctx) {
    const { maxNum, type, files, adgroupData, creativeData } = toRefs(props);
    const { materialVerify } = useMaterialVerify({
      adgroupFormData: adgroupData,
      creativeFormData: creativeData,
    });
    const { isSupportPangleNewImageSize } = useAllowlist();

    /** 图片裁剪容器实例 */
    const cropperNodeArr = ref<any[]>([]);
    const tableNode = ref();

    /** 标记当前处于的阶段 crop 裁剪，validate 校验表格，complete 完成 */
    const pageStep = ref(PageStep.crop);
    const curFileIndex = ref(0);
    const selectedFiles = ref<any[]>([]);
    // const isVisible = computed(() => !!files.value?.length);
    const isHasMulti = computed(() => (files.value?.length ?? 0) > 1);
    const isOverMax = computed(() => (files.value?.length ?? 0) > maxNum.value);
    const countLabel = computed(() => `${files.value?.length ?? '-'}/${maxNum.value}`);

    const isConfirmButtonDisable = computed(
      () => !selectedFiles.value?.every(file => file.uploaderStatus === FileUploadStatus.Success),
    );
    const imageModeSupport = computed(() => [
      MaterialSize.Horizontal,
      MaterialSize.Square,
      MaterialSize.Vertical720,
      ...(isSupportPangleNewImageSize.value
        ? [
            MaterialSize.HorizontalInsertRectangle,
            MaterialSize.HorizontalBannerLarge,
            MaterialSize.HorizontalBannerSmall,
          ]
        : []),
    ]);

    /** 弹窗标题 */
    const dialogTitle = computed(
      () =>
        ({
          [PageStep.validate]: i18n.t('upload_check'),
          [PageStep.uploading]: i18n.t('upload_uploading'),
          [PageStep.complete]: i18n.t('upload_finished'),
        }[pageStep.value]),
    );

    /** 获取用于埋点的 source */
    function getSourceForEventTrack() {
      return props.isUploadCover ? 'video_thumbnail' : 'create';
    }

    /** 移除一个文件 */
    function removeImage(img) {
      curFileIndex.value = 0;
      ctx.emit(
        'change',
        files.value?.filter(item => item.uid !== img.uid),
      );

      trackEvent('crop_image_delete', { upload_source: getSourceForEventTrack() });

      logger.info('crop_image_delete', { upload_source: getSourceForEventTrack() });
    }

    /** 裁剪变化 */
    function onCropperChange(result: { imageCropped: HTMLCanvasElement; image: HTMLImageElement }, file) {
      const { imageCropped, image } = result;
      const isCropped = image.width !== imageCropped.width || image.height !== imageCropped.height;
      file.isCropped = isCropped;
      file.croppedCanvas = result.imageCropped;
      file.croppedUrl = file.croppedCanvas.toDataURL(file.type ?? file.raw.type);
    }

    /** 裁剪确认 */
    async function cropperConfirm() {
      const trackEventId = uuidv4();

      files.value &&
        files.value.forEach((file, index) => {
          const isNeedSizeInfo = !(type.value === 'library');
          file.name = isNeedSizeInfo ? handleImageName(file) : file.name;
          file.width = file.croppedCanvas?.width;
          file.height = file.croppedCanvas?.height;
          file.verify = materialVerify({ type: MaterialType.Image, material: file });
          file.raw = dataURLtoFile(file.croppedUrl || '', file.name);

          /** [start] 埋点 */
          const curCropperNode = cropperNodeArr.value[index];
          const info = curCropperNode?.getSomeInfoForEventTrack() ?? {};
          const { origin_ratio, final_ratio, aspect_available, crop_needed, crop_result } = info;
          const imageDetail = omit(info, [
            'origin_ratio',
            'final_ratio',
            'aspect_available',
            'crop_needed',
            'crop_result',
          ]);
          const eventParam = {
            batch_id: trackEventId,
            upload_source: getSourceForEventTrack(),
            image_detail: JSON.stringify({ imageDetail, final_size: file.raw.size }),
            aspect_available,
            origin_ratio,
            origin_size: file.size,
            final_ratio,
            final_size: file.raw.size,
            crop_needed,
            crop_result,
          };
          trackEvent('crop_image_upload_confirm', eventParam);
          logger.info('crop_image_upload_confirm', eventParam);
          /** [end] 埋点 */
        });

      /** 重名校验 */
      const names = files.value && files.value.map(file => file.name);
      await useCheckNameExistServiceApi(names, MaterialType.Image).then(data => {
        const dupNames: string[] = Object.keys(data).filter(key => data[key]);
        files.value &&
          files.value.forEach(file => {
            if (dupNames.includes(file.name)) {
              file.status = FileUploadStatus.NameDuplicate;
              file.disabled = true;
            }
          });
      });

      pageStep.value = PageStep.validate;
      // 完成重名校验后，选中可选中的部分。
      nextTick(() => tableNode.value && tableNode.value.selectAll());
    }

    /** 设置当前图片 */
    function setActiveImage(index, _image) {
      curFileIndex.value = index;
      trackEvent('crop_image_switch_image', { upload_source: getSourceForEventTrack() });
      logger.info('crop_image_switch_image', { upload_source: getSourceForEventTrack() });
    }

    // TODO: 抽屉关闭;
    /** 弹窗关闭 */
    function clearFiles() {
      trackEvent('crop_image_cancel', { upload_source: getSourceForEventTrack(), upload_num: files.value.length });

      logger.info('crop_image_cancel', {
        upload_source: getSourceForEventTrack(),
        upload_num: files.value.length,
      });
      curFileIndex.value = 0;
      pageStep.value = PageStep.crop;
    }

    function onClose() {
      ctx.emit('close');
    }

    //
    function handleBackCrop() {
      pageStep.value = PageStep.crop;
    }

    /** 取消 */
    function onCancel(file) {
      selectedFiles.value = selectedFiles.value?.filter(item => item !== file);
    }

    // 取消所有上传
    function cancelUpload() {
      selectedFiles.value = [];
    }

    /** 表格选中事件 */
    function onSelectChange(val) {
      selectedFiles.value = val;
    }

    /** 开始上传 */
    function upload() {
      pageStep.value = PageStep.complete;
      selectedFiles.value.forEach(file => ((file as any).uploaderStatus = FileUploadStatus.Uploading));
      ctx.emit('change', selectedFiles.value); // 确保上传只有选中的文件
      ctx.emit('submit');
      // 点击上传后，关闭抽屉，在创编页面上传;
      ctx.emit('close');
      // 开始计时
      ctx.emit('startInternal');
    }

    function changeFileUploaderStatus(name, status) {
      const curFile = selectedFiles.value.find(item => item.name === name) ?? {};
      curFile.uploaderStatus = status;
    }

    function confirm() {
      ctx.emit('confirm');
      selectedFiles.value = [];
      pageStep.value = PageStep.crop;
    }

    onBeforeUpdate(() => {
      cropperNodeArr.value = [];
    });

    return {
      name: componentName,
      tableNode,
      onClose,
      selectedFiles,
      pageStep,
      countLabel,
      curFileIndex,
      dialogTitle,
      cropperNodeArr,

      isHasMulti,
      isOverMax,
      isConfirmButtonDisable,
      cancelUpload,
      upload,
      confirm,
      onCancel,
      clearFiles,
      cropperConfirm,
      setActiveImage,
      removeImage,
      onSelectChange,
      onCropperChange,
      changeFileUploaderStatus,
      handleBackCrop,

      imageModeSupport,
    };
  },
});
